import type { FulfillmentType } from "../model/ArticleTypes";
import type { ItemGroup, ItemsByCategory } from "../components/cart-page/ArticleList";
import { ComparableItem, SortCriterion } from "../model/ArticleSorting";
import { getSortFunctionErrorsFirst } from "../model/ArticleSorting";
import type { ErrorState } from "./CartNotificationStore";
import { MovPerSupplierInfo } from "../model/CommonCartTypes";

export type GroupableItem = {
	description: string;
	displayId?: string;
	categories: Array<string>;
	selectedFulfillmentType?: FulfillmentType | null;
	addedTimestamp: number;
	addedBy?: string;
	supplierId: string;
	bundleId: string;
};
export function filterItems<Item extends GroupableItem>(
	textFilter: string | null | undefined,
	cartItems: Array<Item>,
): Array<Item> {
	const filterResults: Item[] = [];

	if (!textFilter || textFilter.length === 0) {
		return cartItems;
	}

	const tokens: string[] = textFilter.split(" ").filter((token) => {
		return token.length > 0;
	});

	const containsToken = (item, token) => {
		const textWhereToSearch = (item.description || "").toLowerCase() + (item.displayId || "");
		return textWhereToSearch.indexOf(token.toLowerCase()) > -1;
	};

	for (const item of cartItems) {
		let itemContainsAllTokens = true;

		for (const token of tokens) {
			if (!containsToken(item, token)) {
				itemContainsAllTokens = false;
				break;
			}
		}

		if (itemContainsAllTokens) {
			filterResults.push(item);
		}
	}

	return filterResults;
}

export function groupItems<Item extends GroupableItem>(
	allCartItems: Array<Item>,
	sortCriterion: SortCriterion | null | undefined,
	cartErrorState: ErrorState | null | undefined,
	movPerSupplierInfo: MovPerSupplierInfo | undefined,
): ItemsByCategory<Item> {
	let articleGroups: ItemsByCategory<Item>;

	const groupByDeliveryModeFunction = (item: GroupableItem) => {
		switch (item.selectedFulfillmentType) {
			case "FSD":
				return "FSD";

			case "CLICK_AND_COLLECT":
				return "CLICK_AND_COLLECT";

			default:
				return "PARCEL_DELIVERY";
		}
	};

	const sortByDeliveryModeFunction = (a: ItemGroup<Item>, b: ItemGroup<Item>) => {
		const hasWarning = (group) => movPerSupplierInfo?.blockCheckout && group.name === "PARCEL_DELIVERY";

		const aHasWarning = hasWarning(a);
		const bHasWarning = hasWarning(b);

		if (aHasWarning && !bHasWarning) return -1;
		if (!aHasWarning && bHasWarning) return 1;

		const order = ["FSD", "PARCEL_DELIVERY", "CLICK_AND_COLLECT"];
		return order.indexOf(a.name) - order.indexOf(b.name);
	};

	const groupByAdderFunction = (item) => item.addedBy || "";

	const sortByNameFunction = (a: ItemGroup<Item>, b: ItemGroup<Item>) => {
		return a.name.localeCompare(b.name);
	};

	let groupFunction;
	let sortFunction;
	let typeOfGrouping;

	//
	switch (sortCriterion) {
		case "GROUP_BY_DELIVERY_MODE":
			typeOfGrouping = "DELIVERY_MODE";
			groupFunction = groupByDeliveryModeFunction;
			sortFunction = sortByDeliveryModeFunction;
			break;
		case "GROUP_BY_CATEGORY": {
			const deliveryModeArray: string[] = allCartItems.map((item) => groupByDeliveryModeFunction(item));
			//in most scenarios deliveryModesSize is 1 (FSD)
			const deliveryModesSize = new Set(deliveryModeArray).size;
			const groupByCategoryFunction = (item: GroupableItem) => {
				const itemCategories: string = (item.categories && item.categories[0]) || "";
				//itemCategories example: 'level0 / level1 / level2'
				const levels = itemCategories.split("/");
				let itemCategory = levels[1] ? levels[1].trim() : levels[0].trim();
				itemCategory = itemCategory ? itemCategory : "-";

				if (deliveryModesSize === 1) {
					return itemCategory;
				} else {
					return groupByDeliveryModeFunction(item) + "#" + itemCategory;
				}
			};
			typeOfGrouping = "CATEGORY";
			groupFunction = groupByCategoryFunction;
			sortFunction = sortByNameFunction;
			break;
		}
		case "GROUP_BY_ADDER":
			typeOfGrouping = "ADDER";
			groupFunction = groupByAdderFunction;
			sortFunction = sortByNameFunction;
			break;

		default:
			articleGroups = [
				{
					type: "UNGROUPED",
					name: "filtered",
					items: allCartItems,
				},
			];
			return articleGroups;
	}

	const [cartItems, cartItemsWithErrors] = allCartItems.reduce(
		(acc, item) => {
			const [items, itemsWithErrors] = acc;
			if (cartErrorState?.itemErrors.get(item.bundleId)) {
				itemsWithErrors.push(item);
			} else {
				items.push(item);
			}
			return acc;
		},
		[[], []] as [Item[], Item[]],
	);

	const groupedItems = cartItems.reduce((acc: Record<string, ItemGroup<Item>>, item: Item) => {
		const groupBy = groupFunction(item);
		const group = acc[groupBy] || {
			type: typeOfGrouping,
			name: groupBy,
			items: [],
		};
		group.items.push(item);
		acc[groupBy] = group;
		return acc;
	}, {});

	articleGroups = Object.values(groupedItems);
	articleGroups.sort(sortFunction);
	if (cartItemsWithErrors.length > 0) {
		articleGroups.unshift({
			type: typeOfGrouping,
			name: "ERRORS_GROUP",
			items: cartItemsWithErrors,
		});
	}
	return articleGroups;
}

export function sortItems<Item extends ComparableItem>(
	cartErrorState: ErrorState | null | undefined,
	sortCriterion: SortCriterion,
	articleGroups: ItemsByCategory<Item>,
	movPerSupplierInfo: MovPerSupplierInfo | undefined,
) {
	for (const group of articleGroups) {
		if (group.items && group.items.length > 1) {
			const sortFunctionForGroup = getSortFunctionErrorsFirst(
				cartErrorState,
				sortCriterion,
				group,
				movPerSupplierInfo,
			);
			group.items.sort(sortFunctionForGroup);
		}
	}
}
